import type { ProfileFamilyBrand } from '@modules/professional/util/derive-profile-family';
import type { DetailTile } from '~/ui-rocco/pages/detail-shared';
import { getImagesSizes } from '~/utils/image-sizes-utils';

/**
 * The Figma professional tile (`Tile / Professional`, e.g. 5522:96684) as
 * overrides on the shared {@link DetailTile} card: a 437x524 portrait image with
 * a 100px logo badge inset 30px, and names clipped to one line so rows in a grid
 * stay aligned. Shared by the Network tab's connections grid and both
 * brand-family surfaces, so the same brand tiles identically everywhere.
 */
export const PROFESSIONAL_TILE_MEDIA =
    'bg-neutral-200 max-md:aspect-[437/524] md:aspect-[437/524]';

export const PROFESSIONAL_TILE_LOGO =
    'max-md:top-3 max-md:left-3 max-md:size-12 md:top-[1.875rem] md:left-[1.875rem] md:size-[6.25rem]';

/**
 * The caption size the tile system uses.
 *
 * The browse grids' tiles set their title and supplier line at 14px; the
 * detail-page card this profile reuses defaults to 22px, drawn for a page that
 * shows a handful of tiles rather than a grid of them. Side by side the
 * profile's tiles read as a different component, so they take the system's
 * size here. Scoped to the profile rather than changed on the card, which
 * product and project detail also render.
 */
export const PROFILE_TILE_TEXT = 'md:text-[0.875rem] md:leading-[1.3]';

export const PROFESSIONAL_TILE_TEXT = `truncate ${PROFILE_TILE_TEXT}`;

/**
 * Serve the banner at roughly twice the tile's width, not at it. The source is a
 * ~16:10 landscape crop dropped into a portrait box, so `object-cover` scales it
 * to match the box HEIGHT and clips the sides: filling 533px of height off a
 * landscape source needs ~840px of width. Sizing to the 446px tile width instead
 * upscales it vertically and looks soft. Still a quarter of the bytes of the
 * untouched 1920-wide rendition, which matters for a six-brand family.
 */
export const PROFESSIONAL_TILE_SIZES = getImagesSizes({
    desktop: 880,
    mobile: 400,
});

/** A family brand as a tile — the grid and the carousel read the same shape. */
export const brandToTile = (brand: ProfileFamilyBrand): DetailTile => ({
    id: brand.id,
    // Arms the hover overlay. Without a pinType the shared media renders no
    // overlay at all, which left these the one set of tiles on the page with
    // nothing on hover.
    pinType: 'ProfessionalPin',
    libraryItemId: brand.id,
    // A brand is its own professional: the id lets the tile's impression and
    // click carry it, and the subtitle keeps the name off the card.
    professional: { id: brand.id },
    link: brand.link,
    title: brand.title,
    subtitle: brand.meta,
    images: [{ src: brand.image, alt: brand.title }],
    logo: brand.logo || null,
    logoBackground: brand.logoBackground,
    website: brand.website,
});
